import MuiCard from '@mui/material/Card';
import { Box } from '@mui/material';
import {
  ForwardRefExoticComponent,
  forwardRef,
  PropsWithChildren,
  PropsWithoutRef,
  RefAttributes,
} from 'react';

type FR<T, P> = ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>;

type ToneVariant = 'danger' | 'spotlight' | 'major';
type Variant = 'basic' | ToneVariant | 'public';

type BasicProps = PropsWithChildren<{
  variant?: 'basic';
  siloreal?: boolean;
}>;

type PublicProps = PropsWithChildren<{
  variant: 'public';
  siloreal?: boolean;
}>;

type ToneProps = PropsWithChildren<{
  variant: ToneVariant;
  siloreal?: never;
}>;

export type Props = BasicProps | PublicProps | ToneProps;

const VARIANT_SX: Record<Variant, React.ComponentProps<typeof MuiCard>['sx']> = {
  basic: {
    backgroundColor: '#f3f2f7',
    borderRadius: '10px',
  },
  danger: {
    backgroundColor: '#fde8e8',
    borderRadius: '10px',
  },
  spotlight: {
    backgroundColor: '#effeec',
    borderRadius: '10px',
  },
  major: {
    backgroundColor: '#f7effd',
    borderRadius: '10px',
  },
  public: {
    position: 'relative',
    padding: 3,
    borderRadius: 2,
    boxShadow: '0 0 0 1px #6BAA0033',
  },
};

export const Card: FR<HTMLDivElement, Props> = forwardRef((props, ref) => {
  const { children, siloreal = false } = props;
  const variant = props.variant ?? 'basic';
  const cardStyle =
    variant === 'basic' && siloreal
      ? {
          backgroundImage:
            'linear-gradient(92deg, #c8fe9d 0%, rgba(0, 200, 255, 0.24) 27%, rgba(243, 242, 247, 0.96) 44%, #f3f2f7 100%)',
        }
      : undefined;

  return (
    <MuiCard ref={ref} elevation={0} sx={VARIANT_SX[variant]} style={cardStyle}>
      {variant === 'public' && siloreal && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            borderRadius: '8px',
            width: '100%',
            height: '100%',
            padding: '1px',
            pointerEvents: 'none',
            mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0) border-box',
            maskComposite: 'exclude',
          }}
        >
          <Box
            sx={{
              background: 'conic-gradient(from 0deg, transparent 0%, #00c8ff 4%, #6BAA00, transparent 14%)',
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: '1000px',
              height: '1000px',
              transform: 'translate(-50%, -50%)',
              transformOrigin: '0 0',
              animation: 'rotateShine 10s ease-out infinite',
            }}
          />
        </Box>
      )}
      {children}
    </MuiCard>
  );
});
